import type { SelectOption } from '@components';
import type { Theme } from '@mui/material';
import type { SxProps } from '@mui/system';
import type { Query, QueryKey } from '@tanstack/react-query';
import { useField } from 'formik';
import { type ReactElement, useState } from 'react';

import { DEBOUNCE_TIME } from '@core/constants/common';
import { useDebounceValue } from '@core/hooks/useDebounceValue';
import { hasValue } from '@core/utils/equality';
import { isFormReference } from '@core/utils/formikHelpers';
import { ConstructListQueryParams } from '@core/utils/lookupQuery';
import type { NestedKeyOf } from '@core/utils/types';

import { type UseSlabQueryType, useSlabQuery } from '../../hooks/useSlabQuery';
import type { QueryRouteBarrelTypes } from '../../utils/ApiClient';
import type { QueryError } from '../../utils/Query';
import { LookupInput, type LookupInputProps } from './LookupInput';
import type { RouteHelpers } from './utils';

type InputLookupProps<
  QKey extends keyof QueryRouteBarrelTypes,
  QBarrel extends QueryRouteBarrelTypes[QKey] = QueryRouteBarrelTypes[QKey],
  QOptions extends UseSlabQueryType<QKey, QBarrel>['options'] = UseSlabQueryType<
    QKey,
    QBarrel
  >['options'],
> = {
  sx?: SxProps<Theme>;
  route: {
    barrel: QKey;
    args: (inputText: string, helpers: RouteHelpers<QKey>) => QBarrel['args'];
    options?: QOptions;
    transform: (d: QBarrel['returns']) => SelectOption<string>[];
  };
  onMatchChange?: (arg0: string | null, d: QBarrel['returns'] | undefined) => void;
};

/** An API-based single-select lookup input component that integrates with Formik. */
export const ApiLookupInput = <
  FState extends Record<string, any>,
  FPath extends NestedKeyOf<FState>,
  QKey extends keyof QueryRouteBarrelTypes,
  QBarrel extends QueryRouteBarrelTypes[QKey] = QueryRouteBarrelTypes[QKey],
  QOptions extends UseSlabQueryType<QKey, QBarrel>['options'] = UseSlabQueryType<
    QKey,
    QBarrel
  >['options'],
>({
  route,
  onMatchChange = undefined,
  ...props
}: InputLookupProps<QKey, QBarrel, QOptions> &
  Omit<
    LookupInputProps<FState, FPath>,
    | 'options'
    | 'onMatchChange'
    | 'onInputChange'
    | 'isLoading'
    | 'noOptionsText'
    | 'disableClientSideFiltering'
  >): ReactElement => {
  const [{ value: fieldValue }] = useField<unknown>(props.name);
  const formOption = isFormReference(fieldValue) ? fieldValue.option : null;
  const formOptionExists = hasValue(formOption);

  const [lookupText, setLookupText] = useState('');
  // A picked option's label is shown in the input, but is not a search
  const debouncedLookupText = useDebounceValue(formOptionExists ? '' : lookupText, DEBOUNCE_TIME);

  const helpers: RouteHelpers<QKey> = { constructQueryParams: ConstructListQueryParams };
  const routeArgs = route.args(debouncedLookupText, helpers);

  const routeEnabled = route.options?.enabled;
  // If `option` is defined, do NOT make an API request.
  const enabled =
    typeof routeEnabled === 'function'
      ? (query: Query<QBarrel['returns'], QueryError, QBarrel['returns'], QueryKey>): boolean =>
          routeEnabled(query) && !formOptionExists
      : (routeEnabled ?? true) && !formOptionExists;

  const { isLoading, isError, data } = useSlabQuery(route.barrel, routeArgs, {
    // refetchInterval must be explicit for type compilation when spreading.
    refetchInterval: route.options?.refetchInterval,
    ...route.options,
    enabled,
  });

  const options = ((): SelectOption<string>[] => {
    if (hasValue(formOption)) {
      return [formOption];
    }
    return hasValue(data) ? route.transform(data) : [];
  })();

  const hasQueryParams = Object.keys(routeArgs).find((k) => k === 'queryParams') !== undefined;
  return (
    <LookupInput
      {...props}
      options={options}
      isLoading={isLoading}
      noOptionsText={isError ? 'Error loading options' : undefined}
      onMatchChange={(arg0: string | null): void => onMatchChange?.(arg0, data)}
      onInputChange={setLookupText}
      disableClientSideFiltering={hasQueryParams}
    />
  );
};
